import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const JobAlertModal = ({ isOpen, onClose, onSave, alertToEdit = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    keywords: [],
    location: '',
    jobType: 'Any',
    salaryMin: '',
    frequency: 'daily'
  });
  
  const [keywordInput, setKeywordInput] = useState('');
  const keywordInputRef = useRef(null);

  useEffect(() => {
    if (alertToEdit) {
      setFormData({
        name: alertToEdit.name || '',
        keywords: alertToEdit.keywords || [],
        location: alertToEdit.location || '',
        jobType: alertToEdit.jobType || 'Any',
        salaryMin: alertToEdit.salaryMin || '',
        frequency: alertToEdit.frequency || 'daily'
      });
    } else {
      setFormData({
        name: '',
        keywords: [],
        location: '',
        jobType: 'Any',
        salaryMin: '',
        frequency: 'daily'
      });
    }
  }, [alertToEdit, isOpen]);
  
  const addKeyword = () => {
    const trimmedKeyword = keywordInput.trim();
    
    if (!trimmedKeyword) return;
    
    if (formData.keywords.length >= 10) {
      alert('You can only add up to 10 keywords');
      return;
    }
    
    if (formData.keywords.some(k => k.toLowerCase() === trimmedKeyword.toLowerCase())) {
      alert('This keyword is already added');
      setKeywordInput('');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      keywords: [...prev.keywords, trimmedKeyword]
    }));
    setKeywordInput('');
    keywordInputRef.current?.focus();
  };
  
  const removeKeyword = (keywordToRemove) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keywordToRemove)
    }));
  };
  
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    } else if (e.key === 'Backspace' && !keywordInput && formData.keywords.length > 0) {
      removeKeyword(formData.keywords[formData.keywords.length - 1]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const alertData = {
      ...formData,
      salaryMin: parseInt(formData.salaryMin) || 0
    };
    
    if (alertToEdit) {
      alertData.id = alertToEdit.id;
    }
    
    onSave(alertData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ 
          boxShadow: '4px 4px 0 rgba(0, 0, 0, 1)',
          transform: 'translateX(-4px) translateY(-4px)'
        }}>
        <div className="sticky top-0 bg-white border-b-2 border-black p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-black">
            {alertToEdit ? 'Edit Job Alert' : 'Create Job Alert'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-black transition-colors">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alert Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
              placeholder="e.g., Frontend Developer Jobs"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords <span className="text-red-500">*</span>
            </label>
            
            {/* Selected Keywords Tags */}
            {formData.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 rounded-full text-sm font-medium"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeKeyword(keyword)}
                      className="hover:bg-gray-800 rounded-full p-0.5 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            <input
              ref={keywordInputRef}
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              required={formData.keywords.length === 0}
              className="w-full px-4 py-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
              placeholder="Type a keyword and press Enter..."
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">Press Enter to add keyword</p>
              <p className="text-xs text-gray-500">{formData.keywords.length} / 10 keywords</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
                placeholder="e.g., Mumbai, Bangalore, Remote"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Type
              </label>
              <select
                value={formData.jobType}
                onChange={(e) => setFormData({ ...formData, jobType: e.target.value })}
                className="w-full px-4 py-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
              >
                <option value="Any">Any</option>
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Freelance">Freelance</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Salary (₹ LPA)
              </label>
              <input
                type="number"
                value={formData.salaryMin}
                onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
                className="w-full px-4 py-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
                placeholder="e.g., 8"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">Salary in Lakhs Per Annum (LPA)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Frequency
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-4 py-3 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-6 border-t-2 border-black">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-black text-black font-medium hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-yellow-400 text-black font-bold border-2 border-black hover:bg-yellow-500 transition-colors"
              style={{ 
                boxShadow: '3px 3px 0 rgba(0, 0, 0, 1)',
                transform: 'translateX(-3px) translateY(-3px)'
              }}
            >
              {alertToEdit ? 'Update Alert' : 'Create Alert'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobAlertModal;
